import type { ReactNode } from 'react';
import React from 'react';

import type { TabProps, TabsProps } from '@mui/material';
import { Tab, tabClasses, Tabs, tabsClasses, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const StyledTabs = styled(Tabs)<TabsProps>(() => ({
  [`& .${tabsClasses.indicator}`]: {
    borderRadius: '6px 6px 0 0',
    height: 5,
    top: 0,
  },
}));

const StyledTab = styled(Tab)<TabProps>(({ theme }) => ({
  '&:last-of-type': {
    borderRight: `1px solid ${theme.appColors.rule}`,
  },
  [`&:not(.${tabClasses.selected})`]: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
  },
  '&:not(:last-of-type)': {
    borderRight: 'none',
  },
  [`&.${tabClasses.selected}`]: {
    backgroundColor: theme.palette.common.white,
  },
  borderLeft: `1px solid ${theme.appColors.rule}`,
  borderRadius: '6px 6px 0 0',
  borderTop: `1px solid ${theme.appColors.rule}`,
  boxSizing: 'border-box',
  cursor: 'pointer',
  textTransform: 'none',
}));

export type AlineTabProps = TabProps & {
  dataCy?: string;
};

type AlineTabsProps<TabValue> = {
  onChange: (value: TabValue) => void;
  tabs: AlineTabProps[];
  value: string | number | null;
  tabsProps?: TabsProps;
};

export function AlineTabs<TabValue = string>({
  onChange,
  tabs,
  tabsProps,
  value,
}: AlineTabsProps<TabValue>) {
  return (
    <StyledTabs
      {...tabsProps}
      textColor="primary"
      value={value}
      onChange={(_e, value) => onChange(value)}
    >
      {tabs.map(({ dataCy, ...rest }) => {
        return <StyledTab {...rest} key={rest.value} data-cy={dataCy} />;
      })}
    </StyledTabs>
  );
}

const useStyles = makeStyles()(theme => ({
  titleContainer: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    height: '100%',
    paddingRight: theme.spacing(2),
  },
}));

export function PageTitle({ pageTitle }: { pageTitle: ReactNode }) {
  const { classes } = useStyles();

  return (
    <div className={classes.titleContainer}>
      <Typography variant="h2">{pageTitle}</Typography>
    </div>
  );
}
